import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import {
  ArrowRight, Sparkles, CalendarCheck, FileText,
  Camera, ShieldCheck, Layers,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1721244654394-36a7bc2da288?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwYmx1ZXByaW50fGVufDB8fHx8MTc4MTQ3MTEzN3ww&ixlib=rb-4.1.0&q=85";
const FEATURE_IMG =
  "https://images.unsplash.com/photo-1659353586512-bcc4c7182d01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwbWFuYWdlciUyMHRhYmxldHxlbnwwfHx8fDE3ODE0NzExMzd8MA&ixlib=rb-4.1.0&q=85";
const CUSTOMER_IMG =
  "https://images.pexels.com/photos/7579356/pexels-photo-7579356.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const ctaLink = (to) => () => nav(user ? "/dashboard" : to);

  return (
    <div className="min-h-screen" style={{ background: "var(--bq-bg)" }}>
      {/* Nav */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/70 border-b border-[var(--bq-border)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-brand">
            <Logo variant="word" height={32} linkTo={null} testId="landing-brand-logo" />
            <span className="overline hidden sm:inline">South Africa</span>
          </Link>
          <nav className="flex items-center gap-4">
            <a href="#how" className="text-sm text-[var(--bq-text-muted)] hover:text-[var(--bq-navy)] hidden md:inline" data-testid="nav-how">
              How it works
            </a>
            <a href="#trust" className="text-sm text-[var(--bq-text-muted)] hover:text-[var(--bq-navy)] hidden md:inline" data-testid="nav-trust">
              Why us
            </a>
            <Link
              to={user ? "/dashboard" : "/login"}
              className="text-sm font-medium hover:text-[var(--bq-orange)]"
              data-testid="nav-login"
            >
              {user ? "Dashboard" : "Login"}
            </Link>
            <Link
              to={user ? "/projects/new" : "/register"}
              className="bq-orange-btn px-4 py-2 rounded-md text-sm font-semibold"
              data-testid="nav-get-started"
            >
              Get a Quote
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${HERO_IMG}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{ background: "rgba(11,25,44,0.55)" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-white">
          <div className="inline-block mb-4">
            <span
              className="overline"
              style={{
                background: "var(--bq-orange)",
                color: "#fff",
                padding: "4px 10px",
                display: "inline-block",
              }}
              data-testid="hero-tagline"
            >
              From idea to handover · One platform
            </span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-extrabold leading-[1.02] max-w-4xl">
            Build smarter.<br />Quote faster.<br />
            <span className="text-[var(--bq-orange)]">Stop the WhatsApp chaos.</span>
          </h1>
          <p className="mt-6 max-w-xl text-white/85 text-lg">
            Submit your project, get an AI-powered rough estimate in under 60 seconds, book a survey, and approve quotes digitally — all in one professional workspace.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <button
              onClick={ctaLink("/register")}
              className="bq-orange-btn px-6 py-3 rounded-md font-semibold flex items-center gap-2"
              data-testid="hero-get-quote"
            >
              Get a Quote <ArrowRight size={16} />
            </button>
            <button
              onClick={ctaLink("/register")}
              className="px-6 py-3 rounded-md font-semibold border border-white/40 hover:bg-white/10 transition-all"
              data-testid="hero-upload-plans"
            >
              Upload Plans
            </button>
            <button
              onClick={ctaLink("/register")}
              className="px-6 py-3 rounded-md font-semibold border border-white/40 hover:bg-white/10 transition-all"
              data-testid="hero-book-survey"
            >
              Book Site Survey
            </button>
          </div>

          {/* trust strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl">
            <Trust value="< 60s" label="Rough Estimate" />
            <Trust value="24–48h" label="Detailed Quote" />
            <Trust value="POPIA" label="Compliant" />
            <Trust value="ZAR" label="South African pricing" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">
        <div className="overline mb-3">The customer journey</div>
        <h2 className="font-display text-4xl md:text-5xl font-extrabold max-w-3xl">
          Six steps from <span className="text-[var(--bq-orange)]">idea</span> to keys in hand.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--bq-border)] border border-[var(--bq-border)] rounded-lg overflow-hidden">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white p-7 hover:bg-[var(--bq-bg)] transition-all" data-testid={`step-${i + 1}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-md bq-dark flex items-center justify-center text-sm font-mono">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <s.icon size={18} className="text-[var(--bq-orange)]" />
              </div>
              <h3 className="font-display text-xl font-bold">{s.title}</h3>
              <p className="text-sm text-[var(--bq-text-muted)] mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Bento feature section */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 bq-card p-8">
            <div className="overline mb-3">AI Estimator</div>
            <h3 className="font-display text-3xl font-extrabold leading-tight">
              Rough estimates in seconds, not weeks.
            </h3>
            <p className="text-[var(--bq-text-muted)] mt-3">
              Our AI reads your project type, budget bracket, location and uploads — and returns a confidence-scored cost range so you know exactly where you stand before paying for a detailed quote.
            </p>
            <div className="mt-6 bq-grid-bg border border-dashed border-[var(--bq-border)] p-6 rounded-md">
              <div className="overline">Sample estimate</div>
              <div className="font-display text-4xl font-extrabold mt-1 text-[var(--bq-navy)]">R 180,000 – R 240,000</div>
              <div className="mt-2 flex items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1 text-[var(--bq-green)] font-semibold">
                  <ShieldCheck size={14} /> 72% confidence
                </span>
                <span className="text-[var(--bq-text-muted)]">incl. labour, materials, PM</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 bq-card p-0 overflow-hidden flex flex-col">
            <img src={FEATURE_IMG} alt="Construction manager with tablet" className="w-full h-56 object-cover" />
            <div className="p-7 flex-1">
              <div className="overline mb-2">Quote builder</div>
              <h3 className="font-display text-2xl font-extrabold">
                Estimators in control
              </h3>
              <p className="text-sm text-[var(--bq-text-muted)] mt-2">
                Quote categories from demolition to handover, with AI-suggested line items and instant subtotal + VAT calculation.
              </p>
            </div>
          </div>

          <div className="md:col-span-4 bq-card p-7">
            <Camera size={22} className="text-[var(--bq-orange)]" />
            <h4 className="font-display text-xl font-bold mt-3">Survey reports</h4>
            <p className="text-sm text-[var(--bq-text-muted)] mt-2">
              Field-ready measurements, photo evidence and risk notes — all attached to the project record.
            </p>
          </div>

          <div className="md:col-span-4 bq-card p-7">
            <Layers size={22} className="text-[var(--bq-orange)]" />
            <h4 className="font-display text-xl font-bold mt-3">Milestones &amp; payments</h4>
            <p className="text-sm text-[var(--bq-text-muted)] mt-2">
              Deposit 30% / 25 / 25 / 20 — payments auto-generated on quote approval, releases tied to progress.
            </p>
          </div>

          <div className="md:col-span-4 bq-card p-0 overflow-hidden">
            <img src={CUSTOMER_IMG} alt="Happy homeowner" className="w-full h-full object-cover min-h-[160px]" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="trust" className="bq-dark">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-white/70">Ready when you are</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold mt-3">
              Your next build starts here.
            </h2>
            <p className="text-white/70 mt-4 max-w-md">
              Sign up free, submit a project, and have a working estimate before you finish your coffee.
            </p>
          </div>
          <div className="flex md:justify-end gap-3 flex-wrap">
            <button
              onClick={ctaLink("/register")}
              className="bq-orange-btn px-6 py-3 rounded-md font-semibold flex items-center gap-2"
              data-testid="footer-cta-register"
            >
              Create free account <ArrowRight size={16} />
            </button>
            <Link
              to="/login"
              className="px-6 py-3 rounded-md font-semibold border border-white/30 hover:bg-white/10 text-white"
              data-testid="footer-cta-login"
            >
              I have an account
            </Link>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/60">
            <div className="bg-white rounded-lg px-5 py-3 shadow-sm">
              <img
                src="https://customer-assets.emergentagent.com/job_construct-approve-1/artifacts/5rkws481_WhatsApp%20Image%202026-06-15%20at%2008.55.16.jpeg"
                alt="Plan2Build — Upload your plans. We build the rest."
                style={{ height: 90, width: "auto", display: "block" }}
                data-testid="footer-logo"
              />
            </div>
            <div className="flex flex-col items-center md:items-end gap-1">
              <span>© {new Date().getFullYear()} Plan2Build · POPIA aligned</span>
              <span>Built for South African contractors</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const STEPS = [
  { title: "Submit project", desc: "Title, type, location, budget bracket, timeline and uploads — 3 minutes max.", icon: FileText },
  { title: "AI rough estimate", desc: "Instant ZAR cost range with a confidence score.", icon: Sparkles },
  { title: "Request detailed quote", desc: "Optional paid service for a precise itemised quote (refundable).", icon: FileText },
  { title: "Book site survey", desc: "Pick a slot, surveyor arrives, full report attached to your project.", icon: CalendarCheck },
  { title: "Approve digitally", desc: "Review, e-sign, OTP confirm. No paper, no chasing.", icon: ShieldCheck },
  { title: "Track to handover", desc: "Live milestones, progress %, payments, messaging — until keys in hand.", icon: Layers },
];

function Trust({ value, label }) {
  return (
    <div>
      <div className="font-display text-3xl md:text-4xl font-extrabold">{value}</div>
      <div className="overline mt-1" style={{ color: "var(--bq-orange)" }}>{label}</div>
    </div>
  );
}
